import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useT } from '@dia/i18n';
import { loadShell } from '@dia/content';
import {
  browserOffline,
  cacheRows,
  cachedRows,
  newId,
  pendingOps,
  shadowUpdate,
  useCapturePhoto,
  useOutbox,
  type Message,
  type Request,
  type RequestType,
} from '@dia/db';
import { Button, PhotoCapture, StatusPill } from '@dia/ui';
import { useAuth } from '../../auth';
import { HOSTS_WHATSAPP, SUPABASE_ANON_KEY, SUPABASE_URL } from '../../env';
import { supabase, sync } from '../../supabase';
import { TYPES_WITH_TIME, buildRequestRow } from '../logic/requestForm';
import { POLL_MS, shownText } from '../logic/thread';
import { translateText } from '../logic/translateClient';
import { buildRequestMessage, buildWhatsAppLink } from '../logic/whatsapp';
import {
  ASK_TYPES,
  buildHelpThread,
  cardFromRow,
  guestStatusKey,
  guestStatusTone,
  lateShadowJobs,
  messageFromRow,
  queuedItems,
  requestTypeFor,
  type ComposerMode,
  type HelpThreadItem,
  type MessageItem,
  type RequestCard,
} from '../logic/helpThread';
import { DocArticle } from './Prose';
import { BellIcon, FlagIcon } from './shellIcons';
import { markThreadSeen } from './useUnreadReplies';

const content = loadShell('guest');

const TYPE_KEY = {
  boat: 'guest.request.type.boat',
  provisioning: 'guest.request.type.provisioning',
  maintenance: 'guest.request.type.maintenance',
  housekeeping: 'guest.request.type.housekeeping',
  concierge: 'guest.request.type.concierge',
  other: 'guest.request.type.other',
} as const;

const MODES: readonly ComposerMode[] = ['message', 'ask', 'problem'];

const MODE_KEY = {
  message: 'guest.thread.mode.message',
  ask: 'guest.help.ask',
  problem: 'guest.help.problem',
} as const;

/**
 * The one help thread: Requests and Mensajes merged, the way a website support
 * widget works (docs/GUEST-REDESIGN.md, "How Requests and Messages fit
 * together"). The help sheet draws it, and so do the two old addresses,
 * /pedidos and /mensajes, which keep working because no address is removed.
 *
 * One thread per stay with the hosts. Three ways to add a line to it:
 *
 *   a message    a bubble, exactly as Mensajes always was,
 *   a request    "Ask for something": what, and when if it matters,
 *   a problem    "Report a problem": what is broken, and a photo if they like.
 *
 * A request or a problem becomes a card with its status in guest words
 * (Received, On it, Done). The crew still get the tidy structured row in the
 * ops queue, because that is exactly the row that is written, the same one the
 * old Requests form wrote.
 *
 * What did not change: every line goes to the outbox first, so it is on the
 * phone at once and shows "Saved, waiting to send" until the island has it.
 * Each line is shown in the reader's language with a tap for the original. The
 * thread asks the island for anything new every 30 seconds while it is open,
 * and not while the tab is hidden. It is not a chat: no typing dots, no read
 * receipts, no live transport. WhatsApp is there for the truly urgent, and the
 * button stays hidden until the hosts' real number is set.
 *
 * The caller has already checked there is a session inside the access window.
 */
export function HelpThread({ initialMode }: { initialMode: ComposerMode }) {
  const { t, lang } = useT();
  const { session } = useAuth();
  const { enqueue, pending, online } = useOutbox(sync);
  const stayId = session?.stay.id;

  const [items, setItems] = useState<HelpThreadItem[]>([]);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const endRef = useRef<HTMLDivElement | null>(null);

  const [mode, setMode] = useState<ComposerMode>(initialMode);
  const [askType, setAskType] = useState<RequestType>('boat');
  const [text, setText] = useState('');
  const [when, setWhen] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [saved, setSaved] = useState<null | 'request' | 'message'>(null);

  // Following another way in (the sheet's rows, or ?tipo=problema) switches the composer.
  useEffect(() => {
    setMode(initialMode);
    setError('');
    setSaved(null);
  }, [initialMode]);

  // A problem report's photos go under its own id, so the folder is known
  // before the report is sent and the storage policy can read the stay.
  const [draftId, setDraftId] = useState(newId);
  const [photos, setPhotos] = useState<Array<{ id: string; path: string }>>([]);
  const photoParts = useMemo(() => ({ stayId, requestId: draftId }), [stayId, draftId]);
  const capture = useCapturePhoto({ bucket: 'request-photos', parts: photoParts }, sync);

  // Loads can overlap (a poll and a sent line); only the newest one may paint.
  const loadSeq = useRef(0);

  // A line that went with no signal (or on a slow one) reached the island in
  // English only. Once the island has it, this phone asks for the Spanish and
  // queues it through the outbox, so the crew read it in their language. The
  // server row is the reminder (see lateShadowJobs), so a closed app or a dead
  // battery only delays it to the next time the thread is opened with signal.
  // A few at a time, each tried once per visit unless it simply failed.
  const lateTried = useRef<Set<string>>(new Set());
  const lateBusy = useRef(false);
  const fillLateShadows = useRef<(messages: Message[], requests: Request[]) => Promise<void>>(async () => {});
  fillLateShadows.current = async (messages, requests) => {
    // One pass at a time: a poll and a sent line can both finish a load at once.
    if (!session || browserOffline() || lateBusy.current) return;
    lateBusy.current = true;
    try {
      const jobs = lateShadowJobs({ messages, requests }, session.guest.id, await pendingOps())
        .filter((job) => !lateTried.current.has(job.clientId))
        .slice(0, 5);
      for (const job of jobs) {
        lateTried.current.add(job.clientId);
        const result = await translateText({
          url: SUPABASE_URL,
          anonKey: SUPABASE_ANON_KEY,
          token: session.access_token,
          text: job.text,
          from: 'en',
          to: 'es',
          audience: 'staff',
          timeoutMs: 8000,
        });
        if (!result.ok) {
          // A spent allowance or an ended session will not mend in 30 seconds;
          // anything else is tried again on the next look.
          if (result.reason !== 'quota' && result.reason !== 'session') lateTried.current.delete(job.clientId);
          if (result.reason === 'offline') return;
          continue;
        }
        await enqueue(shadowUpdate(job.table, { clientId: job.clientId, lang: 'es', text: result.text }));
      }
    } finally {
      lateBusy.current = false;
    }
  };

  const load = useCallback(async () => {
    if (!stayId) return;
    const seq = ++loadSeq.current;

    const paint = async (messages: Message[], requests: Request[]) => {
      const known = {
        messages: new Set(messages.map((m) => m.client_id)),
        requests: new Set(requests.map((r) => r.client_id)),
      };
      const queued = queuedItems(await pendingOps(), known, stayId);
      if (seq !== loadSeq.current) return;
      setItems(buildHelpThread([...messages.map(messageFromRow), ...requests.map(cardFromRow), ...queued]));
    };

    // What the phone already has, straight away: the last saved thread plus
    // anything still waiting to send. A failed fetch can take seconds, and the
    // guest should never stare at an empty thread while it does.
    const savedMessages = (await cachedRows<Message>('messages:stay')).filter((m) => m.stay_id === stayId);
    const savedRequests = (await cachedRows<Request>('requests:mine')).filter((r) => r.stay_id === stayId);
    await paint(savedMessages, savedRequests);

    // With no network at all there is nothing to ask (defect D6).
    if (browserOffline()) return;

    const [messages, requests] = await Promise.all([
      (async () => {
        const { data, error: err } = await supabase
          .from('messages')
          .select('*')
          .eq('thread_kind', 'stay')
          .eq('stay_id', stayId)
          .order('created_at', { ascending: true });
        if (err) throw new Error(err.message);
        return cacheRows('messages:stay', (data ?? []) as Message[]);
      })().catch(() => null),
      (async () => {
        const { data, error: err } = await supabase
          .from('requests')
          .select('*')
          .eq('stay_id', stayId)
          .order('created_at', { ascending: true });
        if (err) throw new Error(err.message);
        return cacheRows('requests:mine', (data ?? []) as Request[]);
      })().catch(() => null),
    ]);
    if (seq !== loadSeq.current) return;
    if (messages && requests) setCheckedAt(Date.now());
    await paint(messages ?? savedMessages, requests ?? savedRequests);
    if (messages && requests) void fillLateShadows.current(messages, requests);
  }, [stayId]);

  // Poll every 30 seconds while the thread is open and visible. The first load
  // is the effect below, which also runs on mount.
  useEffect(() => {
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', onVisible);
    const clock = setInterval(() => setNow(Date.now()), 30_000);
    return () => {
      clearInterval(poll);
      clearInterval(clock);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  // Now, and again when a queued line lands or the connection returns.
  useEffect(() => {
    void load();
  }, [load, pending, online]);

  // Looking at the thread is reading it: the gold dot goes out on the way in
  // and again on the way out, so a reply read here does not light it later.
  useEffect(() => {
    if (!stayId) return;
    markThreadSeen(stayId);
    return () => markThreadSeen(stayId);
  }, [stayId]);

  // The newest line in view, once there is one. An empty thread leaves the page
  // where it is, so the title and the lede are what the guest sees first.
  useEffect(() => {
    if (items.length > 0) endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [items.length]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!stayId || !session) return;
    const body = text.trim();
    if (!body) {
      setError(t('guest.request.empty_text'));
      return;
    }
    setError('');
    setSending(true);
    setSaved(null);

    // The hosts and the crew read Spanish. Try for a shadow, briefly, and never
    // let it hold the write: with no signal the line goes as written.
    let shadow: string | null = null;
    if (lang === 'en') {
      const result = await translateText({
        url: SUPABASE_URL,
        anonKey: SUPABASE_ANON_KEY,
        token: session.access_token,
        text: body,
        from: 'en',
        to: 'es',
        audience: 'staff',
        timeoutMs: 2500,
      });
      if (result.ok) shadow = result.text;
    }

    if (mode === 'message') {
      await enqueue({
        table: 'messages',
        payload: {
          thread_kind: 'stay',
          stay_id: stayId,
          author_kind: 'guest',
          author_guest_id: session.guest.id,
          text_original: body,
          lang_original: lang,
          text_es: lang === 'es' ? body : shadow,
          text_en: lang === 'en' ? body : null,
        },
      });
      setSaved('message');
    } else {
      await enqueue({
        id: draftId,
        table: 'requests',
        payload: buildRequestRow({
          draft: { type: requestTypeFor(mode, askType), text: body, when },
          lang,
          stayId,
          translation: shadow ? { lang: 'es', text: shadow } : null,
          photoPaths: mode === 'problem' ? photos.map((p) => p.path) : [],
        }),
      });
      setWhen('');
      setPhotos([]);
      setDraftId(newId());
      setSaved('request');
    }

    setText('');
    setSending(false);
    await load();
  };

  const toggle = (id: string) =>
    setRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const time = (iso: string) =>
    new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Panama' }).format(
      new Date(iso),
    );

  const agoLabel = () => {
    if (checkedAt === null) return '';
    const minutes = Math.max(0, Math.floor((now - checkedAt) / 60_000));
    const ago = minutes < 1 ? (lang === 'es' ? 'un momento' : 'a moment') : `${minutes} min`;
    return t('guest.messages.checked', { ago });
  };

  const requestType = mode === 'message' ? null : requestTypeFor(mode, askType);
  const greeting = t('guest.request.whatsapp_greeting');
  const urgentLink = buildWhatsAppLink(
    HOSTS_WHATSAPP,
    requestType
      ? buildRequestMessage({
          greeting,
          typeLabel: t(TYPE_KEY[requestType]),
          details: text,
          firstName: session?.guest.first_name,
          when: when ? when.replace('T', ' ') : undefined,
        })
      : greeting,
  );

  const tierNotes = content.forTier(session?.stay.tier ?? null).filter((d) => d.section === 'requests');

  const submitLabel =
    mode === 'message'
      ? t('guest.messages.send')
      : mode === 'problem'
        ? t('guest.thread.problem_submit')
        : t('guest.request.submit');

  return (
    <div className="flex flex-col gap-6">
      {!online && (
        <p className="text-inkFaint text-caption" role="status">
          {t('guest.messages.offline')}
        </p>
      )}
      {checkedAt !== null && <p className="text-inkFaint text-caption">{agoLabel()}</p>}

      {items.length === 0 && <p className="text-inkMuted">{t('guest.thread.empty')}</p>}

      <ol className="m-0 flex list-none flex-col gap-4 p-0" aria-live="polite">
        {items.map((item) =>
          item.kind === 'request' ? (
            <CardLine
              key={item.id}
              card={item}
              lang={lang}
              time={time}
              revealed={revealed.has(item.id)}
              onToggle={() => toggle(item.id)}
            />
          ) : (
            <MessageLine
              key={item.id}
              message={item}
              under={item.under !== null}
              lang={lang}
              time={time}
              revealed={revealed.has(item.id)}
              onToggle={() => toggle(item.id)}
            />
          ),
        )}
      </ol>
      <div ref={endRef} />

      <form onSubmit={onSubmit} className="flex flex-col gap-5 border-t border-line pt-6">
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2">{t('guest.thread.mode_label')}</legend>
          <div className="flex flex-wrap gap-2">
            {MODES.map((value) => (
              <label
                key={value}
                className={`inline-flex min-h-tap cursor-pointer items-center rounded-full border px-4 py-2 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-accent ${
                  mode === value ? 'border-accent bg-accent/10 text-ink' : 'border-line text-inkMuted'
                }`}
              >
                <input
                  type="radio"
                  name="thread-mode"
                  value={value}
                  checked={mode === value}
                  onChange={() => {
                    setMode(value);
                    setError('');
                    setSaved(null);
                  }}
                  className="sr-only"
                />
                {t(MODE_KEY[value])}
              </label>
            ))}
          </div>
        </fieldset>

        {mode === 'ask' && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2">{t('guest.request.type_label')}</legend>
            {ASK_TYPES.map((value) => (
              <label
                key={value}
                className={`flex min-h-tap items-center gap-3 rounded border px-4 py-2 ${
                  askType === value ? 'border-accent text-ink' : 'border-line text-inkMuted'
                }`}
              >
                <input
                  type="radio"
                  name="request-type"
                  value={value}
                  checked={askType === value}
                  onChange={() => setAskType(value)}
                  className="h-5 w-5 accent-[rgb(var(--c-accent))]"
                />
                <span>{t(TYPE_KEY[value])}</span>
              </label>
            ))}
          </fieldset>
        )}

        {mode === 'ask' && tierNotes.length > 0 && (
          <details className="rounded border border-line px-4 py-2">
            <summary className="min-h-tap flex cursor-pointer items-center text-accent">
              {t('guest.thread.ask_notes')}
            </summary>
            <div className="flex flex-col gap-6 pb-3 pt-2">
              {tierNotes.map((doc) => (
                <DocArticle key={doc.path} doc={doc} level={3} />
              ))}
            </div>
          </details>
        )}

        {mode === 'problem' && <p className="text-inkMuted">{t('guest.request.problem_lede')}</p>}

        <label className="flex flex-col gap-2">
          <span>{mode === 'message' ? t('guest.messages.label') : t('guest.request.text_label')}</span>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={mode === 'message' ? 3 : 4}
            maxLength={1000}
            className="bg-surface border border-line rounded px-4 py-3 text-ink"
          />
          {mode !== 'message' && <span className="text-inkFaint text-caption">{t('guest.request.text_help')}</span>}
        </label>

        {mode === 'problem' && (
          <div className="flex flex-col gap-2">
            <span>{t('guest.request.photo_label')}</span>
            {/* Keyed by the draft, so a sent report leaves an empty picker behind it. */}
            <PhotoCapture
              key={draftId}
              label={t('guest.request.photo_label')}
              max={3}
              onCapture={async (photo) => {
                const { path } = await capture(photo.file);
                setPhotos((prev) => [...prev, { id: photo.id, path }]);
              }}
              onRemove={(id) => setPhotos((prev) => prev.filter((p) => p.id !== id))}
            />
            <span className="text-inkFaint text-caption">{t('guest.request.photo_help')}</span>
          </div>
        )}

        {requestType && TYPES_WITH_TIME.has(requestType) && (
          <label className="flex flex-col gap-2">
            <span>{t('guest.request.when_label')}</span>
            <input
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              className="min-h-tap bg-surface border border-line rounded px-4 text-ink"
            />
            <span className="text-inkFaint text-caption">{t('guest.request.when_help')}</span>
          </label>
        )}

        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}

        <Button type="submit" block size="lg" busy={sending} busyLabel={t('loading')}>
          {submitLabel}
        </Button>

        {saved && (
          <p role="status" className="text-ok">
            {online && pending === 0
              ? saved === 'message'
                ? t('guest.thread.message_sent')
                : t('guest.request.sent')
              : t('guest.request.saved')}
          </p>
        )}
      </form>

      {urgentLink && (
        <p className="flex flex-col gap-2 text-inkMuted">
          <span>{t('guest.messages.urgent')}</span>
          <a
            href={urgentLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center min-h-tap rounded border border-line px-5 py-3 text-ink hover:border-accent"
          >
            {t('guest.request.whatsapp_button')}
          </a>
        </p>
      )}
    </div>
  );
}

type LineProps = {
  lang: 'es' | 'en';
  time: (iso: string) => string;
  revealed: boolean;
  onToggle: () => void;
};

/** The words of a line in the reader's language, with a tap for the original. */
function Words({
  line,
  lang,
  revealed,
  onToggle,
  mine,
}: LineProps & { line: Pick<MessageItem, 'original' | 'originalLang' | 'es' | 'en'>; mine: boolean }) {
  const { t } = useT();
  const shown = shownText(line, lang);
  const missingShadow = line.originalLang !== lang && !shown.translated;
  return (
    <>
      <p className="m-0 whitespace-pre-wrap break-words">{revealed ? shown.original : shown.text}</p>
      {shown.translated && (
        <button
          type="button"
          onClick={onToggle}
          className="text-accent underline underline-offset-4 text-caption min-h-tap text-left"
        >
          {revealed ? t('lang.show_translation') : t('lang.show_original')}
        </button>
      )}
      {missingShadow && !mine && <span className="text-inkFaint text-caption">{t('guest.messages.no_translation')}</span>}
    </>
  );
}

function MessageLine({ message, under, ...rest }: LineProps & { message: MessageItem; under: boolean }) {
  const { t } = useT();
  const mine = message.from === 'guest';
  return (
    <li
      className={`flex flex-col gap-1 max-w-[88%] ${mine ? 'self-end items-end' : 'self-start items-start'} ${
        under ? 'ml-4 border-l-2 border-accent/40 pl-3' : ''
      }`}
    >
      {under && <span className="sr-only">{t('guest.thread.reply_to')}</span>}
      <div className={`rounded px-4 py-3 border bg-surface text-ink ${mine ? 'border-accent' : 'border-line'}`}>
        <Words line={message} mine={mine} {...rest} />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-caption text-inkFaint">
        <span>{mine ? t('guest.messages.you') : t('guest.messages.hosts')}</span>
        <span>{rest.time(message.createdAt)}</span>
        {message.pending && (
          <StatusPill tone="warn" glyph="!">
            {t('sync.saved_pending')}
          </StatusPill>
        )}
      </div>
    </li>
  );
}

/**
 * A request or a problem report, as a card in the thread. The status is a word
 * as well as a color, and "Saved, waiting to send" takes its place while the
 * card is still only on this phone.
 */
function CardLine({ card, ...rest }: LineProps & { card: RequestCard }) {
  const { t } = useT();
  const Icon = card.type === 'maintenance' ? FlagIcon : BellIcon;
  return (
    <li className="flex w-full max-w-[92%] flex-col items-end gap-1 self-end">
      <div className="flex w-full flex-col gap-2 rounded-[1.125rem] border border-accent/60 bg-surface px-4 py-3 text-ink">
        <div className="flex flex-wrap items-center gap-2">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[0.7rem] bg-accent/10 text-accent">
            <Icon className="h-[1.1rem] w-[1.1rem]" />
          </span>
          <span className="font-semibold">{t(TYPE_KEY[card.type])}</span>
          <span className="ml-auto">
            {card.pending ? (
              <StatusPill tone="warn" glyph="!">
                {t('sync.saved_pending')}
              </StatusPill>
            ) : (
              <StatusPill tone={guestStatusTone(card.status)}>{t(guestStatusKey(card.status))}</StatusPill>
            )}
          </span>
        </div>
        <Words line={card} mine {...rest} />
        {card.dueAt && (
          <p className="m-0 text-caption text-inkMuted">{t('guest.thread.card_when', { when: rest.time(card.dueAt) })}</p>
        )}
        {card.photoCount > 0 && (
          <p className="m-0 text-caption text-inkMuted">{t('guest.thread.card_photos', { count: card.photoCount })}</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-caption text-inkFaint">
        <span>{t('guest.messages.you')}</span>
        <span>{rest.time(card.createdAt)}</span>
      </div>
    </li>
  );
}
